import { useEffect, useRef, useState } from "react";
import {
  BellRing,
  CalendarClock,
  Download,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Upload,
  UserRound,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Expander } from "@/components/Expander";
import { MeetingFormDialog } from "@/components/MeetingFormDialog";
import { api, apiError, downloadFile, fmtDay, fmtWhen } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KIND_COLOUR = {
  "MoS Review": "hsl(var(--primary))",
  SteerCo: "hsl(268 60% 58%)",
  "DP Review": "hsl(160 84% 34%)",
  "Business Entity Meeting": "hsl(36 92% 44%)",
};
const LEAD_WORD = { 0: "on the morning", 1: "1 day before", 2: "2 days before",
                    3: "3 days before", 7: "a week before" };

const Row = ({ meeting, onEdit, onRemove, onRemind, canWrite, canRemind, showMinutes }) => {
  const colour = KIND_COLOUR[meeting.kind] || "hsl(var(--primary))";
  return (
    <div
      className="bg-card p-4"
      style={{ borderTop: `3px solid ${colour}` }}
      data-testid={`meeting-${meeting.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <span
            className="font-display text-[10px] font-bold uppercase tracking-widest"
            style={{ color: colour }}
          >
            {meeting.kind}
          </span>
          <div className="text-sm font-bold">{meeting.title || meeting.kind}</div>
          <div className="font-data mt-1.5 space-y-0.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <CalendarClock className="h-3 w-3" /> {fmtDay(meeting.date)}
              {meeting.time ? ` · ${meeting.time} IST` : ""}
              {meeting.when_label ? ` · ${meeting.when_label}` : ""}
            </span>
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3" /> {meeting.mode}
              {meeting.venue ? ` · ${meeting.venue}` : ""}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="h-3 w-3" /> {meeting.units.join(", ") || "All of SAIL"}
            </span>
            {meeting.chaired_by && (
              <span className="flex items-center gap-1.5">
                <UserRound className="h-3 w-3" /> {meeting.chaired_by}
              </span>
            )}
          </div>
        </div>
        {canWrite && (
          <div className="flex shrink-0 items-center gap-1">
            {canRemind && (
              <button
                type="button"
                title="Tell everybody now"
                data-testid={`meeting-remind-${meeting.id}`}
                onClick={() => onRemind(meeting)}
                className="border border-border p-1.5 text-muted-foreground transition-colors hover:border-primary hover:text-primary"
              >
                <BellRing className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              title="Correct it"
              data-testid={`meeting-edit-${meeting.id}`}
              onClick={() => onEdit(meeting)}
              className="border border-border p-1.5 text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Take it off the cadence"
              data-testid={`meeting-remove-${meeting.id}`}
              onClick={() => onRemove(meeting)}
              className="border border-border p-1.5 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {meeting.agenda && (
        <p className="mt-2 border-l-2 border-border pl-2 text-[11px] text-muted-foreground">
          {meeting.agenda}
        </p>
      )}
      {meeting.minutes && showMinutes && (
        <p className="mt-2 text-[11px]" data-testid={`meeting-minutes-${meeting.id}`}>
          <b>What came out of it: </b>
          <span className="text-muted-foreground">{meeting.minutes}</span>
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
        {meeting.reminder_days.map((day) => (
          <span
            key={day}
            data-testid={`meeting-lead-${meeting.id}-${day}`}
            className={`border px-1.5 py-0.5 uppercase tracking-wider ${
              meeting.reminded?.includes(String(day))
                ? "border-[hsl(var(--healthy))] text-[hsl(var(--healthy))]"
                : "border-border text-muted-foreground"
            }`}
          >
            {LEAD_WORD[day] || `${day} days before`}
            {meeting.reminded?.includes(String(day)) ? " · sent" : ""}
          </span>
        ))}
        {meeting.status !== "scheduled" && (
          <span
            data-testid={`meeting-status-${meeting.id}`}
            className="border border-border px-1.5 py-0.5 uppercase tracking-wider text-muted-foreground"
          >
            {meeting.status}
          </span>
        )}
        {!!meeting.invitees.length && (
          <span className="text-muted-foreground">
            {meeting.invitees.length} officer(s) named on it
          </span>
        )}
        {meeting.last_reminded_at && (
          <span
            data-testid={`meeting-told-${meeting.id}`}
            className="border border-[hsl(var(--healthy))] px-1.5 py-0.5 uppercase tracking-wider text-[hsl(var(--healthy))]"
          >
            Told by hand · {fmtWhen(meeting.last_reminded_at)}
          </span>
        )}
      </div>
    </div>
  );
};

export default function Meetings() {
  const { shows } = useAuth();
  const picker = useRef(null);
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("all");
  const [plant, setPlant] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ open: false, meeting: null });
  const [remind, setRemind] = useState(null);
  const [byMail, setByMail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);

  const load = () => {
    api
      .get("/meetings", {
        params: {
          kind: kind === "all" ? "" : kind,
          plant: plant === "all" ? "" : plant,
          q,
        },
      })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, plant, q]);

  const remove = async (meeting) => {
    if (!window.confirm(`Are you sure? The ${meeting.kind} of ${meeting.date} will be taken off.`))
      return;
    try {
      await api.delete(`/meetings/${meeting.id}`);
      toast.success("Taken off the cadence.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openRemind = async (meeting) => {
    setByMail(false);
    setRemind({ meeting, who: null });
    try {
      const { data: said } = await api.post(`/meetings/${meeting.id}/remind`, { dry_run: true });
      setRemind({ meeting, who: said.told });
    } catch (e) {
      toast.error(apiError(e));
      setRemind(null);
    }
  };

  const sendRemind = async () => {
    setBusy(true);
    try {
      const { data: said } = await api.post(`/meetings/${remind.meeting.id}/remind`, {
        by_mail: byMail,
        dry_run: false,
      });
      toast.success(
        `${said.bell} officer(s) told on the portal` +
          (said.mailed ? ` and ${said.mailed} by mail.` : "."),
      );
      setRemind(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const pullTemplate = async () => {
    setBusy(true);
    try {
      await downloadFile("/meetings/template", {}, "meeting_cadence.xlsx");
      toast.success("Fill the sheet and bring it back — nothing saves until you confirm.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const readSheet = async (chosen) => {
    if (!chosen) return;
    setBusy(true);
    try {
      const sheet = new FormData();
      sheet.append("file", chosen);
      const { data: said } = await api.post("/meetings/import", sheet);
      setFile(chosen);
      setPreview(said.preview);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (picker.current) picker.current.value = "";
    }
  };

  const applySheet = async () => {
    setBusy(true);
    try {
      const sheet = new FormData();
      sheet.append("file", file);
      const { data: said } = await api.post("/meetings/import?apply=true", sheet);
      toast.success(
        `${said.applied.added} new · ${said.applied.corrected} corrected` +
          (said.applied.refused ? ` · ${said.applied.refused} row(s) refused.` : "."),
      );
      setPreview(null);
      setFile(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-8" data-testid="meetings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Meeting cadence</div>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
            When we meet, and on what
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            The MoS Review, the SteerCo, the DP Review and each unit's Business Entity Meeting, on
            one cadence. SDTD schedules them; every SPOC the meeting covers is told on the portal's
            bell and by mail on the lead times the meeting itself carries — two days before and the
            morning of it, unless SDTD says otherwise.
          </p>
        </div>
        {data.can_write && (
          <div className="flex flex-wrap items-center gap-2">
            {shows("meetings.schedule") && (
              <Button
                data-testid="add-meeting-button"
                onClick={() => setForm({ open: true, meeting: null })}
                className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
              >
                <Plus className="mr-2 h-4 w-4" /> Schedule a meeting
              </Button>
            )}
            {shows("meetings.past") && (
              <>
                <Button
                  variant="outline"
                  data-testid="meetings-template"
                  disabled={busy}
                  onClick={pullTemplate}
                  className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                >
                  <Download className="mr-2 h-4 w-4" /> Template
                </Button>
                <input
                  ref={picker}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  data-testid="meetings-file"
                  onChange={(e) => readSheet(e.target.files?.[0])}
                />
                <Button
                  variant="outline"
                  data-testid="meetings-upload"
                  disabled={busy}
                  onClick={() => picker.current?.click()}
                  className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                >
                  <Upload className="mr-2 h-4 w-4" /> Load past meetings
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-px bg-border lg:grid-cols-6">
        {[
          ["Coming up", data.counts.upcoming, "meeting-stat-upcoming"],
          ["Inside 7 days", data.counts.this_week, "meeting-stat-week"],
          ["On record", data.counts.past, "meeting-stat-past"],
          ...data.kinds.map((k) => [k, data.counts.by_kind[k] || 0, `meeting-stat-${k}`]),
        ].map(([label, value, tid]) => (
          <div key={label} className="bg-card p-4" data-testid={tid}>
            <div className="label-caps">{label}</div>
            <div className="font-data mt-2 text-2xl font-bold">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Input
          placeholder="Search a subject, an agenda or a venue…"
          className="h-11 max-w-sm rounded-none bg-card"
          data-testid="meeting-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-11 w-[240px] rounded-none bg-card" data-testid="meeting-kind-filter">
            <SelectValue placeholder="Which review" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Every review</SelectItem>
            {data.kinds.map((k) => (
              <SelectItem key={k} value={k}>
                {k}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={plant} onValueChange={setPlant}>
          <SelectTrigger className="h-11 w-[200px] rounded-none bg-card" data-testid="meeting-unit-filter">
            <SelectValue placeholder="Unit" />
          </SelectTrigger>
          <SelectContent className="max-h-72 rounded-none bg-popover">
            <SelectItem value="all">Any unit</SelectItem>
            {data.plants.map((p) => (
              <SelectItem key={p.code} value={p.code}>
                {p.code} — {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <h2 className="font-display mt-8 border-b-2 border-foreground pb-2 text-xl font-black tracking-tight">
        Coming up
      </h2>
      <div className="mt-px grid gap-px bg-border lg:grid-cols-2" data-testid="meetings-upcoming">
        {data.upcoming.map((m) => (
          <Row
            key={m.id}
            meeting={m}
            canWrite={data.can_write}
            canRemind={shows("meetings.remind")}
            showMinutes={shows("meetings.minutes")}
            onEdit={(meeting) => setForm({ open: true, meeting })}
            onRemove={remove}
            onRemind={openRemind}
          />
        ))}
        {!data.upcoming.length && (
          <p className="bg-card p-6 text-sm text-muted-foreground" data-testid="meetings-empty">
            Nothing scheduled yet.
            {data.can_write ? ' Press "Schedule a meeting" to put the first one on.' : ""}
          </p>
        )}
      </div>

      <div className="mt-6">
        <Expander
          title={`On record · ${data.past.length}`}
          note="Meetings that have been held, with what came out of them."
          testId="meetings-past-expander"
        >
          <div className="grid gap-px bg-border lg:grid-cols-2">
            {data.past.map((m) => (
              <Row
                key={m.id}
                meeting={m}
                canWrite={data.can_write}
                canRemind={shows("meetings.remind")}
                showMinutes={shows("meetings.minutes")}
                onEdit={(meeting) => setForm({ open: true, meeting })}
                onRemove={remove}
                onRemind={openRemind}
              />
            ))}
            {!data.past.length && (
              <p className="bg-card p-4 text-sm text-muted-foreground">
                Nothing on record yet.
              </p>
            )}
          </div>
        </Expander>
      </div>

      <MeetingFormDialog
        open={form.open}
        onOpenChange={(o) => setForm((f) => ({ ...f, open: o }))}
        meeting={form.meeting}
        kinds={data.kinds}
        kindHelp={data.kind_help}
        modes={data.modes}
        plants={data.plants}
        leads={data.reminder_choices}
        onSaved={load}
      />

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto rounded-none bg-card sm:max-w-2xl"
          data-testid="meetings-preview"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">
              This is what the sheet would put on the cadence
            </DialogTitle>
            <DialogDescription className="text-xs">
              {file?.name} · {preview?.total} meeting(s) read. Everything comes in as held, with its
              reminders struck off, so no unit is ever written to about a meeting that is over.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <p className="font-data">
              {preview?.new?.length || 0} new · {preview?.corrected?.length || 0} corrected ·{" "}
              {preview?.refused?.length || 0} refused
            </p>
            {!!preview?.new?.length && (
              <ul className="space-y-0.5 text-muted-foreground">
                {preview.new.slice(0, 12).map((m) => (
                  <li key={`${m.kind}-${m.date}`}>
                    <b className="text-foreground">{m.kind}</b> · {fmtDay(m.date)}
                    {m.title ? ` — ${m.title}` : ""}
                  </li>
                ))}
              </ul>
            )}
            {!!preview?.corrected?.length && (
              <p className="text-muted-foreground">
                Already on the record, so corrected rather than duplicated:{" "}
                {preview.corrected.map((m) => `${m.kind} ${m.date}`).join(", ")}
              </p>
            )}
            {!!preview?.refused?.length && (
              <ul className="space-y-0.5 text-[hsl(var(--watch))]" data-testid="meetings-refused">
                {preview.refused.slice(0, 8).map((r) => (
                  <li key={r.row}>
                    Row {r.row}: {r.kind} / {r.date} — {r.why}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className="h-11 rounded-none"
              data-testid="meetings-import-cancel"
              onClick={() => setPreview(null)}
            >
              Leave it
            </Button>
            <Button
              className="h-11 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
              data-testid="meetings-import-confirm"
              disabled={busy || !preview?.total}
              onClick={applySheet}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save them
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!remind} onOpenChange={(o) => !o && setRemind(null)}>
        <DialogContent className="rounded-none bg-card sm:max-w-lg" data-testid="remind-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">
              Tell everybody now
            </DialogTitle>
            <DialogDescription className="text-xs">
              {remind?.meeting?.kind} · {fmtDay(remind?.meeting?.date)}. The bell is harmless; a
              mail goes to real SAIL officers, so tick that only when you mean it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div className="border border-border p-3">
              <div className="label-caps">
                Who it reaches · {remind?.who?.length ?? "reading…"}
              </div>
              <div
                className="font-data mt-2 max-h-40 space-y-0.5 overflow-y-auto text-[11px] text-muted-foreground"
                data-testid="remind-recipients"
              >
                {(remind?.who || []).map((email) => (
                  <div key={email}>{email}</div>
                ))}
                {remind?.who && !remind.who.length && (
                  <span>Nobody — no SPOC is on the portal for those units yet.</span>
                )}
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="remind-by-mail"
                checked={byMail}
                onChange={(e) => setByMail(e.target.checked)}
              />
              Send a mail as well as the bell
            </label>
          </div>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className="h-11 rounded-none"
              data-testid="remind-cancel"
              onClick={() => setRemind(null)}
            >
              Leave it
            </Button>
            <Button
              className="h-11 rounded-none font-display text-[11px] font-bold uppercase tracking-widest"
              data-testid="remind-send"
              disabled={busy || !remind?.who}
              onClick={sendRemind}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Tell them
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
